import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Lock, CheckCircle2, LogOut } from "lucide-react";
import { toast } from "sonner";
import wtaLogo from "@/assets/wta-logo.png";
import { isReleased, formatReleaseDate } from "@/lib/lessonSchedule";

type HomeworkQuestion = {
  id: string;
  label: string;
  type: "long" | "short";
  required?: boolean;
  minLength?: number;
};

type LessonRow = {
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  video_url: string | null;
  video_path: string | null;
  homework_intro: string | null;
  homework_questions: HomeworkQuestion[];
  order_index: number;
};

const toEmbedUrl = (url: string): string | null => {
  try {
    const u = new URL(url);
    let videoId: string | null = null;
    if (u.hostname.includes("youtu.be")) {
      videoId = u.pathname.replace("/", "");
    } else if (u.hostname.includes("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) {
        videoId = u.pathname.replace("/embed/", "");
      } else {
        videoId = u.searchParams.get("v");
      }
    }
    if (!videoId) return url;
    // Clean embed: no related videos from other channels, no YouTube logo click,
    // modest branding, no keyboard shortcuts to open on YouTube, no fullscreen redirect.
    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      iv_load_policy: "3",
      disablekb: "1",
    });
    if (typeof window !== "undefined") {
      params.set("origin", window.location.origin);
    }
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  } catch {
    return null;
  }
};

const Lesson = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [lesson, setLesson] = useState<LessonRow | null>(null);
  const [prevSlug, setPrevSlug] = useState<string | null>(null);
  const [prevTitle, setPrevTitle] = useState<string | null>(null);
  const [nextSlug, setNextSlug] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [notReleased, setNotReleased] = useState(false);
  const [existing, setExisting] = useState<Record<string, string> | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackAt, setFeedbackAt] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [signedVideoUrl, setSignedVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login", { replace: true });
        return;
      }
      setUserId(session.user.id);

      const { data: enrollment } = await supabase
        .from("enrollments")
        .select("id")
        .eq("product_id", "wta_academy_enrollment")
        .in("status", ["paid", "scholarship"])
        .or(`user_id.eq.${session.user.id},email.eq.${session.user.email}`)
        .limit(1)
        .maybeSingle();
      if (!enrollment) {
        navigate("/members", { replace: true });
        return;
      }

      const { data: allLessons, error: lessonsError } = await supabase
        .from("lessons")
        .select("slug, title, subtitle, description, video_url, video_path, homework_intro, homework_questions, order_index")
        .eq("is_published", true)
        .order("order_index", { ascending: true });

      if (lessonsError) {
        toast.error("We couldn't load this lesson yet. Please refresh and try again.");
        setLoading(false);
        return;
      }

      const current = (allLessons || []).find((l) => l.slug === slug) as LessonRow | undefined;
      if (!current) {
        navigate("/members", { replace: true });
        return;
      }
      setLesson(current);

      // Time gate: lessons release Mondays at noon ET starting July 6, 2026.
      if (!isReleased(current.order_index)) {
        setNotReleased(true);
        setLoading(false);
        return;
      }


      // If the lesson has a native video file, mint a short-lived signed URL.
      if (current.video_path) {
        const { data: signed } = await supabase
          .storage
          .from("lesson-videos")
          .createSignedUrl(current.video_path, 60 * 60 * 4);
        setSignedVideoUrl(signed?.signedUrl ?? null);
      } else {
        setSignedVideoUrl(null);
      }

      const priors = (allLessons || []).filter((l) => l.order_index < current.order_index);
      const next = (allLessons || []).find((l) => l.order_index === current.order_index + 1);
      setNextSlug(next?.slug ?? null);
      const immediatePrev = priors[priors.length - 1];
      setPrevSlug(immediatePrev?.slug ?? null);
      setPrevTitle(immediatePrev?.title ?? null);

      // Gate: must have a submission for every prior lesson that HAS homework questions.
      const gatingSlugs = priors
        .filter((l) => Array.isArray(l.homework_questions) && (l.homework_questions as unknown[]).length > 0)
        .map((l) => l.slug);
      if (gatingSlugs.length > 0) {
        const { data: subs } = await supabase
          .from("homework_submissions")
          .select("lesson_slug")
          .eq("user_id", session.user.id)
          .in("lesson_slug", gatingSlugs);
        const done = new Set((subs || []).map((s) => s.lesson_slug));
        if (gatingSlugs.some((s) => !done.has(s))) {
          setLocked(true);
          setLoading(false);
          return;
        }
      }

      const { data: mine } = await supabase
        .from("homework_submissions")
        .select("responses, feedback, feedback_at")
        .eq("user_id", session.user.id)
        .eq("lesson_slug", current.slug)
        .maybeSingle();
      if (mine?.responses) {
        setExisting(mine.responses as Record<string, string>);
        setResponses(mine.responses as Record<string, string>);
      }
      if (mine?.feedback) {
        setFeedback(mine.feedback as string);
        setFeedbackAt((mine.feedback_at as string) || null);
      }

      setLoading(false);
    };
    init();
  }, [slug, navigate]);

  const embedUrl = useMemo(() => (lesson?.video_url ? toEmbedUrl(lesson.video_url) : null), [lesson?.video_url]);
  const questions = lesson?.homework_questions ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lesson || !userId) return;

    for (const q of questions) {
      const v = (responses[q.id] || "").trim();
      if (q.required && !v) {
        toast.error(`Please answer: ${q.label}`);
        return;
      }
      if (q.minLength && v.length < q.minLength) {
        toast.error(`Please expand your answer to: ${q.label}`);
        return;
      }
      if (v.length > 5000) {
        toast.error(`Answer too long: ${q.label}`);
        return;
      }
    }

    setSubmitting(true);
    const { error } = await supabase
      .from("homework_submissions")
      .upsert(
        { user_id: userId, lesson_slug: lesson.slug, responses, submitted_at: new Date().toISOString() },
        { onConflict: "user_id,lesson_slug" }
      );
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(existing ? "Homework updated" : "Homework submitted — next lesson unlocked");
    setExisting(responses);
    if (!existing && nextSlug) {
      setTimeout(() => navigate(`/members/${nextSlug}`), 900);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-gold/10 bg-background/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/members" className="flex items-center gap-3">
            <img src={wtaLogo} alt="WTA" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-semibold text-gradient-gold">Members</span>
          </Link>
          <button
            onClick={async () => { await supabase.auth.signOut(); navigate("/", { replace: true }); }}
            className="flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-10 md:py-14 max-w-4xl space-y-10">
        <Link to="/members" className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm">
          <ArrowLeft size={16} /> All modules
        </Link>

        {notReleased ? (
          <div className="border border-gold/20 bg-card/60 p-10 text-center space-y-4">
            <Lock className="w-10 h-10 text-gold mx-auto" />
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">Not yet released</h1>
            <p className="text-muted-foreground font-body">
              This lesson unlocks {lesson ? formatReleaseDate(lesson.order_index) : "soon"}. New lessons open every Monday at 12:00 PM ET.
            </p>
            <Link
              to="/members"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gold/40 text-gold font-body text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold/10"
            >
              Back to modules
            </Link>
          </div>
        ) : locked ? (
          <div className="border border-gold/20 bg-card/60 p-10 text-center space-y-4">
            <Lock className="w-10 h-10 text-gold mx-auto" />
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">This lesson is locked</h1>
            <p className="text-muted-foreground font-body">
              Complete the homework for <span className="text-foreground">{prevTitle}</span> before you unlock this one.
            </p>
            {prevSlug && (
              <Link
                to={`/members/${prevSlug}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.2em]"
              >
                Go to {prevTitle}
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.4em] text-gold font-body">
                {lesson!.order_index === 0 ? "Module" : `Lesson ${lesson!.order_index}`}
              </p>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground">{lesson!.title}</h1>
              {lesson!.subtitle && (
                <p className="text-muted-foreground font-body text-lg italic">{lesson!.subtitle}</p>
              )}
            </div>

            {signedVideoUrl ? (
              <div className="w-full min-h-[220px] md:min-h-[430px] border border-gold/20 bg-black overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                <video
                  key={signedVideoUrl}
                  src={signedVideoUrl}
                  controls
                  controlsList="nodownload noremoteplayback"
                  disablePictureInPicture
                  playsInline
                  preload="metadata"
                  className="w-full h-full bg-black"
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            ) : embedUrl ? (
              <div className="w-full min-h-[220px] md:min-h-[430px] border border-gold/20 bg-black overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                <iframe
                  src={embedUrl}
                  title={lesson!.title}
                  className="w-full h-full"
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            ) : (
              <div className="border border-gold/20 bg-card/60 p-10 text-center">
                <p className="text-muted-foreground font-body">
                  {lesson!.description || "Content coming soon."}
                </p>
              </div>
            )}

            {lesson!.description && (signedVideoUrl || embedUrl) && (
              <p className="text-muted-foreground font-body leading-relaxed">{lesson!.description}</p>
            )}

            {questions.length > 0 && (
              <form onSubmit={handleSubmit} className="border border-gold/20 bg-card/60 p-6 md:p-10 space-y-8">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h2 className="font-display text-2xl font-bold text-foreground">Homework</h2>
                    {existing && <CheckCircle2 className="w-5 h-5 text-gold" />}
                  </div>
                  {lesson!.homework_intro && (
                    <p className="text-muted-foreground font-body leading-relaxed">{lesson!.homework_intro}</p>
                  )}
                </div>

                {feedback && (
                  <div className="border border-gold/40 bg-gold/5 p-5 space-y-2">
                    <p className="text-xs uppercase tracking-[0.3em] text-gold font-body">Feedback from Angela</p>
                    <p className="font-body text-sm text-foreground whitespace-pre-wrap leading-relaxed">{feedback}</p>
                    {feedbackAt && (
                      <p className="text-xs text-muted-foreground font-body">
                        {new Date(feedbackAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    )}
                  </div>
                )}


                {questions.map((q, idx) => (
                  <div key={q.id} className="space-y-2">
                    <label className="block font-body text-sm text-foreground">
                      <span className="text-gold mr-2">{String(idx + 1).padStart(2, "0")}.</span>
                      {q.label}
                    </label>
                    <textarea
                      value={responses[q.id] || ""}
                      onChange={(e) => setResponses((r) => ({ ...r, [q.id]: e.target.value }))}
                      rows={q.type === "short" ? 2 : 5}
                      maxLength={5000}
                      className="w-full bg-background border border-gold/20 focus:border-gold/60 outline-none p-3 font-body text-sm text-foreground"
                      required={q.required}
                    />
                  </div>
                ))}

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.2em] disabled:opacity-60"
                  >
                    {submitting ? "Saving..." : existing ? "Update answers" : "Submit & unlock next"}
                  </button>
                  {existing && nextSlug && (
                    <Link
                      to={`/members/${nextSlug}`}
                      className="px-6 py-3 border border-gold/40 text-gold font-body text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold/10"
                    >
                      Continue →
                    </Link>
                  )}
                </div>
              </form>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Lesson;
